<template>
  <fieldset
    class="filter-options"
    :class="`filter-options--${filter.type}`"
  >
    <legend
      v-if="filter?.label"
      class="visually-hidden"
    >
      {{ filter.label }}
    </legend>
    <component
      :is="optionalFilterableList"
      :placeholder="filter.searchBoxPlaceholder"
      @filter="(query) => search = query"
    >
      <ul class="filterable__list filter-options__list">
        <FilterOption
          v-for="option in limitedOptions"
          :key="option.key"
          :filter="filter"
          :option="option"
          @option-toggle="optionToggle"
        />
      </ul>
    </component>

    <div v-if="search && !limitedOptions.length && filter.searchBoxNoResults">
      <p>{{ filter.searchBoxNoResults }}</p>
    </div>

    <div
      v-if="filter.options && filter.options.length > filter.numberUnfoldedOptions && filter.numberUnfoldedOptions > 0"
      class="filter-options__toggles"
    >
      <button
        type="button"
        class="filter-options__toggle"
        :aria-label="$t('Show all {name} options', { name: filter?.label })"
        @click="showAll = !showAll"
      >
        <template v-if="showAll">
          <IconMin class="filter-options__icon icon icon--min" />
          <span v-if="filter.collapseText">
            {{ filter.collapseText }}
          </span>
          <span v-else>{{ $t('Show less options') }}</span>
        </template>
        <template v-else-if="filter.options.length > filter.numberUnfoldedOptions">
          <IconPlus class="filter-options__icon icon icon--min" />
          <span v-if="filter.expandText">
            {{ filter.expandText }}
          </span>
          <span v-else>{{ $t('Show all options') }}</span>
        </template>
      </button>
    </div>

    <button
      type="button"
      class="filter-options__apply"
      :aria-label="$t('Apply {name} filter', { name: filter?.label })"
      @click="$emit('filter-apply')"
    >
      {{ $t('Apply') }}
    </button>

    <button
      type="button"
      class="filter-options__reset"
      :aria-label="$t('Remove {name} filter', { name: filter?.label })"
      @click="$emit('filter-reset')"
    >
      {{ $t('Reset') }}
    </button>
  </fieldset>
</template>

<script lang="ts" setup>
import UiFilterableList from '~layers/app/components/Ui/FilterableList.vue'

const props = defineProps({
  filter: {
    type: Object,
    required: true,
  },
})

const optionalFilterableList = computed(() => {
  if (!props.filter.isShowSearchBox) {
    return 'div'
  }
  if (props.filter.limitOptionsShowSearchBox > 0 && props.filter.limitOptionsShowSearchBox > props.filter.options?.length) {
    return 'div'
  }
  return UiFilterableList
})

const emit = defineEmits(['filter-apply', 'filter-reset', 'option-toggle'])

const { filtersApply } = useDataLayer()

const optionToggle = (selectedFilter: any) => {
  const { option, filter } = selectedFilter

  const action = option.selected ? 'remove' : 'add'

  filtersApply([
    {
      label: filter.label,
      value: option.label,
      action,
    },
  ])

  emit('option-toggle', option.key)
  emit('filter-apply')
}

const showAll = ref(false)
const search = ref('')

const limitedOptions = computed(() => {
  const options = [
    ...(props.filter?.options || []),
  ]
    .filter(option => search.value ? option.label.toLowerCase().includes(search.value.toLowerCase()) : true)
    .filter(option => props.filter.isNrOfResultsVisible === false || option.count > 0)

  if (options.length && props.filter.numberUnfoldedOptions) {
    if (showAll.value) {
      return options
    }

    return options.slice(0, props.filter.numberUnfoldedOptions)
  }

  return options
})
</script>

<style>
@import url("~layers/app/components/Filter/Options.css");
</style>
